import * as _ from 'lodash';
import { AfterContentInit, Component, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
    BehaviorSubject,
    combineLatest,
    fromEvent,
    merge,
    Observable,
    Subject,
    Subscription,
    forkJoin,
    of
} from 'rxjs';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    mapTo,
    shareReplay,
    switchMap,
    tap,
    withLatestFrom
} from 'rxjs/operators';

import { FcpButtonSizes, Type as AlertType } from 'fc-ui-components';

import {
    BulkDownloadImagesInformation,
    BulkDownloadImagesStatistics,
    BulkSearchResult,
    BulkSearchResultWithInformation
} from 'app/content/models/product.model';
import { AutoUnsubscribe, ImageResolution, ofType, platformAvailableLanguages } from 'helpers';
import { Plan, SegmentService, UserService, WindowService, LanguageService } from 'services';
import { DownloadInitiator } from 'services/tracking/download-initiator';
import { ContentService } from '../content.service';
import { MAX_ARTICLE_NUMBER_DOWNLOAD_LIMIT, MAX_GTIN_DOWNLOAD_LIMIT } from '../constants';
import { ProductService } from 'app/content/services/product.service';
import { VideoDeliveryFeatureService } from 'app/content/services/video-delivery-feature.service';
import {
    BulkDownloadToastSearchBy,
    BulkDownloadToastSelection,
    BulkDownloadToastService,
    isBulkDownloadTooManyGtinsError,
    withBulkDownloadCsvEmptyResponseInformation
} from 'app/content/services/bulk-download-toast.service';

type BulkDownloadStreamResult = (BulkSearchResult | BulkSearchResultWithInformation) & {
    selection?: BulkDownloadToastSelection;
    bulkDownloadFailed?: boolean;
    bulkDownloadSearchBy?: BulkDownloadToastSearchBy;
};

type BulkDownloadStreamResultWithInformation = BulkSearchResultWithInformation & {
    selection?: BulkDownloadToastSelection;
};

@Component({
    selector: 'fc-bulk-gtin-search',
    templateUrl: './bulk-search.component.html',
    styleUrls: ['./bulk-search.component.less'],
    standalone: false
})
@AutoUnsubscribe
export class BulkGtinSearchComponent implements OnInit, AfterContentInit {
    @ViewChild('input', { static: true }) private input: ElementRef;
    @Input() isInMainContentDrawer = false;

    public itemsLimitValidation: Observable<{ showNotification: boolean; lastItem: string }>;
    public itemsFormatValidation: Observable<{
        showNotificationAllInvalid: boolean;
        showNotificationSomeInvalid: boolean;
        invalidGtinsSample: string[];
        invalidGtinsAmount: number;
        totalGtinsAmount: number;
    }>;
    public isInputValid: Observable<boolean>;
    public noImagesFound: Observable<boolean>;
    public missingImagesInDownload: Observable<boolean | BulkDownloadImagesInformation>;
    public imagesRestrictions: Observable<{ downloaded: number; total: number }>;
    public hasImagesRestrictions: Observable<boolean>;
    public missingProductDataInDownload: Observable<boolean | BulkDownloadImagesInformation>;
    public isErrorMessageVisible: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public downloadInformation: Observable<boolean | BulkDownloadImagesInformation>;
    public downloadTrigger: Subject<{ type: DownloadEventType }> = new Subject();
    public selectedSearchType: BehaviorSubject<SearchType>;
    public isGtinsSearch: Observable<boolean>;
    public isArticleNumbersSearch: Observable<boolean>;
    public searchType = SearchType;
    public downloadEventType = DownloadEventType;
    public alertType = AlertType;
    public selectedCsvDownloadType = false;
    public selectedImageDownloadType = true;
    public selectedVideoDownloadType = false;
    public readonly videoDownloadEnabled = inject(VideoDeliveryFeatureService).enabled;
    public maxDownloadLimitGtin = MAX_GTIN_DOWNLOAD_LIMIT;
    public maxDownloadLimitArticleNumber = MAX_ARTICLE_NUMBER_DOWNLOAD_LIMIT;
    public readonly resolutionOptions = [
        { value: ImageResolution.Original, text: 'DOWNLOADS.SIZE.ORIGINAL' },
        { value: ImageResolution.Large, text: 'DOWNLOADS.SIZE.LARGE' },
        { value: ImageResolution.Medium, text: 'DOWNLOADS.SIZE.MEDIUM' },
        { value: ImageResolution.Thumb, text: 'DOWNLOADS.SIZE.SMALL' }
    ];
    public resolutionOptionsTranslated;
    public csvLanguageOptions;
    public csvLanguageOptionsTranslated;
    public buttonSize = FcpButtonSizes;
    public multiDownloadForm: FormGroup;
    public subscriptions = new Subscription();

    constructor(
        public route: ActivatedRoute,
        private productService: ProductService,
        private userService: UserService,
        private windowService: WindowService,
        private segmentService: SegmentService,
        private contentService: ContentService,
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private bulkDownloadToastService: BulkDownloadToastService
    ) {
        this.selectedSearchType = new BehaviorSubject(SearchType.ARTICLE_NUMBERS);
    }

    ngOnInit() {
        if (!this.isInMainContentDrawer) {
            this.contentService.setShowSummaryBar(false);
        }

        this.isGtinsSearch = this.selectedSearchType.pipe(
            map((searchType) => searchType === SearchType.GTINS),
            shareReplay()
        );

        this.isArticleNumbersSearch = this.selectedSearchType.pipe(
            map((searchType) => searchType === SearchType.ARTICLE_NUMBERS),
            shareReplay()
        );

        this.csvLanguageOptions = [
            {
                value: CsvLanguageOption.UserPreferredLanguage,
                text: platformAvailableLanguages[this.userService.getPreferredLanguage()].name,
                hint: 'BULK_GTIN_SEARCH.CSV_LANGUAGE_OPTIONS.PREFERRED_LANGUAGE.HINT'
            },
            {
                value: CsvLanguageOption.All,
                text: 'BULK_GTIN_SEARCH.CSV_LANGUAGE_OPTIONS.ALL_AVAILABLE.TEXT',
                hint: 'BULK_GTIN_SEARCH.CSV_LANGUAGE_OPTIONS.ALL_AVAILABLE.HINT'
            }
        ];

        const resolutionOptionsTranslationKeys = this.resolutionOptions.map((option) => option.text);
        const csvLanguageOptionsTranslationKeys = this.csvLanguageOptions.map((option) => option.text);

        this.subscriptions.add(
            forkJoin({
                resolutionTranslations: this.languageService.translateObservable(resolutionOptionsTranslationKeys),
                csvLanguageTranslations: this.languageService.translateObservable(csvLanguageOptionsTranslationKeys)
            }).subscribe(({ resolutionTranslations, csvLanguageTranslations }) => {
                this.resolutionOptionsTranslated = this.resolutionOptions.map((option) => ({
                    ...option,
                    label: resolutionTranslations[option.text]
                }));

                this.csvLanguageOptionsTranslated = this.csvLanguageOptions.map((option) => ({
                    ...option,
                    label: csvLanguageTranslations[option.text]
                }));

                this.initForm();
            })
        );
    }

    ngAfterContentInit() {
        const input: Observable<string> = fromEvent(this.input.nativeElement, 'input').pipe(
            map((event: Event) => (event.target as HTMLInputElement).value),
            debounceTime(500),
            distinctUntilChanged(),
            shareReplay()
        );

        const parsedItems: Observable<{ items: string[]; searchType: SearchType }> = combineLatest([
            input,
            this.selectedSearchType
        ]).pipe(
            map(([inputValue, searchType]) => ({
                searchType,
                items: _.filter(_.split(inputValue, /[,;\n]+/))
            })),
            shareReplay()
        );

        const sortedItems: Observable<{ items: { valid: string[]; invalid: string[] }; searchType: SearchType }> =
            parsedItems.pipe(
                map(({ items, searchType }) => {
                    const slicedItems = _.slice(items, 0, this.getMaxAmountLimit(searchType));

                    return {
                        searchType,
                        items:
                            searchType === SearchType.GTINS
                                ? this.productService.validateGtins(slicedItems)
                                : {
                                      valid: slicedItems,
                                      invalid: []
                                  }
                    };
                }),
                shareReplay()
            );

        this.itemsLimitValidation = parsedItems.pipe(
            map(({ items, searchType }) => {
                const maxAmount = this.getMaxAmountLimit(searchType);
                const showNotification = _.size(items) > maxAmount;
                const lastItem = showNotification ? items[maxAmount - 1] : '';

                return { showNotification, lastItem };
            }),
            shareReplay()
        );

        this.itemsFormatValidation = sortedItems.pipe(
            map(({ items: { valid, invalid } }) => ({
                showNotificationAllInvalid: _.isEmpty(valid) && !_.isEmpty(invalid),
                showNotificationSomeInvalid: !_.isEmpty(valid) && !_.isEmpty(invalid),
                invalidGtinsSample: _.slice(invalid, 0, 3),
                invalidGtinsAmount: _.size(invalid),
                totalGtinsAmount: _.size(valid) + _.size(invalid)
            })),
            shareReplay()
        );

        this.isInputValid = combineLatest([this.itemsFormatValidation, parsedItems]).pipe(
            map(([{ showNotificationAllInvalid }, { items }]) => !showNotificationAllInvalid && !_.isEmpty(items)),
            shareReplay()
        );

        const downloadCsv = this.downloadTrigger.pipe(
            ofType<DownloadEventType>(DownloadEventType.DOWNLOAD_CSV),
            withLatestFrom(sortedItems),
            map(
                ([
                    eventEmittedValue,
                    {
                        searchType,
                        items: { valid }
                    }
                ]) => ({
                    searchType,
                    validItems: valid,
                    selection: this.snapshotDownloadSelection(DownloadEventType.DOWNLOAD_CSV)
                })
            ),
            tap(({ searchType, validItems }) => this.addTracking(searchType, validItems)),
            switchMap(({ searchType, validItems, selection }) =>
                this.productService
                    .bulkDownload({
                        [searchType === SearchType.GTINS ? 'gtins' : 'articleNumbers']: validItems,
                        userLanguage: this.userService.getPreferredLanguage(),
                        size: this.multiDownloadForm.controls.selectedResolution.value,
                        csvLanguage: this.multiDownloadForm.controls.selectedCsvLanguage.value,
                        initiator: DownloadInitiator.BulkDownload
                    })
                    .pipe(
                        map((result) =>
                            withBulkDownloadCsvEmptyResponseInformation(
                                result,
                                this.toBulkDownloadSearchBy(searchType),
                                validItems.length
                            )
                        ),
                        catchError((error) => {
                            console.error('CSV download error:', error);
                            const isTooManyGtinsError = isBulkDownloadTooManyGtinsError(error);
                            if (!isTooManyGtinsError) {
                                this.isErrorMessageVisible.next(true);
                            }
                            return of({
                                downloadUrl: '',
                                tooManyGtins: isTooManyGtinsError,
                                bulkDownloadFailed: !isTooManyGtinsError,
                                bulkDownloadSearchBy: this.toBulkDownloadSearchBy(searchType),
                                downloadInformation: undefined,
                                selection
                            });
                        }),
                        map((result) => ({ ...result, selection }))
                    )
            ),
            shareReplay()
        );

        const downloadImages = this.downloadTrigger.pipe(
            ofType<DownloadEventType>(DownloadEventType.DOWNLOAD_IMAGES),
            withLatestFrom(sortedItems),
            map(
                ([
                    eventEmittedValue,
                    {
                        searchType,
                        items: { valid }
                    }
                ]) => ({
                    searchType,
                    validItems: valid,
                    selection: this.snapshotDownloadSelection(DownloadEventType.DOWNLOAD_IMAGES)
                })
            ),
            tap(({ searchType, validItems }) => this.addTracking(searchType, validItems)),
            switchMap(({ searchType, validItems, selection }) =>
                this.productService
                    .bulkImagesDownload({
                        [searchType === SearchType.GTINS ? 'gtins' : 'articleNumbers']: validItems,
                        size: this.multiDownloadForm.controls.selectedResolution.value,
                        initiator: DownloadInitiator.BulkDownload,
                        images: selection.images,
                        video: selection.video
                    })
                    .pipe(
                        catchError((error) => {
                            console.error('Images download error:', error);
                            const isTooManyGtinsError = isBulkDownloadTooManyGtinsError(error);
                            if (!isTooManyGtinsError) {
                                this.isErrorMessageVisible.next(true);
                            }
                            return of({
                                downloadUrl: '',
                                tooManyGtins: isTooManyGtinsError,
                                bulkDownloadFailed: !isTooManyGtinsError,
                                bulkDownloadSearchBy: this.toBulkDownloadSearchBy(searchType),
                                downloadInformation: undefined,
                                selection
                            });
                        }),
                        map((result) => ({ ...result, selection }))
                    )
            ),
            shareReplay()
        );

        const downloadImagesWithCsv = this.downloadTrigger.pipe(
            ofType<DownloadEventType>(DownloadEventType.DOWNLOAD_IMAGES_WITH_CSV),
            withLatestFrom(sortedItems),
            map(
                ([
                    eventEmittedValue,
                    {
                        searchType,
                        items: { valid }
                    }
                ]) => ({
                    searchType,
                    validItems: valid,
                    selection: this.snapshotDownloadSelection(DownloadEventType.DOWNLOAD_IMAGES_WITH_CSV)
                })
            ),
            tap(({ searchType, validItems }) => this.addTracking(searchType, validItems)),
            switchMap(({ searchType, validItems, selection }) =>
                this.productService
                    .bulkImagesDownloadWithCsv({
                        [searchType === SearchType.GTINS ? 'gtins' : 'articleNumbers']: validItems,
                        userLanguage: this.userService.getPreferredLanguage(),
                        size: this.multiDownloadForm.controls.selectedResolution.value,
                        csvLanguage: this.multiDownloadForm.controls.selectedCsvLanguage.value,
                        initiator: DownloadInitiator.BulkDownload,
                        images: selection.images,
                        video: selection.video
                    })
                    .pipe(
                        catchError((error) => {
                            console.error('Images with CSV download error:', error);
                            const isTooManyGtinsError = isBulkDownloadTooManyGtinsError(error);
                            if (!isTooManyGtinsError) {
                                this.isErrorMessageVisible.next(true);
                            }
                            return of({
                                downloadUrl: '',
                                tooManyGtins: isTooManyGtinsError,
                                bulkDownloadFailed: !isTooManyGtinsError,
                                bulkDownloadSearchBy: this.toBulkDownloadSearchBy(searchType),
                                downloadInformation: undefined,
                                selection
                            });
                        }),
                        map((result) => ({ ...result, selection }))
                    )
            ),
            shareReplay()
        );

        this.subscriptions.add(
            merge(downloadImages, downloadCsv, downloadImagesWithCsv).subscribe({
                next: (result: BulkDownloadStreamResult) => {
                    void this.bulkDownloadToastService
                        .handleBulkDownloadResponse(result, () => {
                            this.windowService.location = result.downloadUrl;
                        })
                        .catch((error) => console.error('Bulk download toast failed', error));
                },
                error: (error) => {
                    console.error('Download error:', error);
                    this.isErrorMessageVisible.next(true);
                    void this.bulkDownloadToastService
                        .handleBulkDownloadResponse(
                            { downloadUrl: '', tooManyGtins: false, bulkDownloadFailed: true },
                            () => undefined
                        )
                        .catch((toastError) => console.error('Bulk download toast failed', toastError));
                }
            })
        );

        this.downloadInformation = merge(
            merge(downloadCsv, downloadImages, downloadImagesWithCsv).pipe(
                filter(({ tooManyGtins }) => !tooManyGtins),
                filter(this.isBulkSearchResultWithInformation),
                map(({ downloadInformation }) => downloadInformation),
                distinctUntilChanged(),
                shareReplay(1)
            ),
            this.selectedSearchType.pipe(mapTo(false)),
            this.isErrorMessageVisible.pipe(mapTo(false))
        ).pipe(shareReplay());

        this.noImagesFound = merge(
            merge(downloadImages, downloadImagesWithCsv).pipe(
                filter(({ tooManyGtins }) => !tooManyGtins),
                map(({ downloadUrl }) => _.isEmpty(downloadUrl))
            ),
            this.selectedSearchType.pipe(mapTo(false)),
            this.isErrorMessageVisible.pipe(mapTo(false))
        ).pipe(shareReplay());

        this.missingImagesInDownload = this.downloadInformation.pipe(
            filter(this.isBulkDownloadImagesInformation),
            map((downloadInformation) => (downloadInformation?.notFoundImages > 0 ? downloadInformation : false)),
            shareReplay()
        );

        this.imagesRestrictions = merge(downloadImages, downloadImagesWithCsv).pipe(
            filter(this.isBulkSearchResultWithInformation),
            filter(
                (
                    result
                ): result is BulkDownloadStreamResultWithInformation & {
                    additionalData: BulkDownloadImagesStatistics;
                } => !_.isUndefined(result.additionalData)
            ),
            map(({ additionalData: { allowedCount, restrictedCount } }) => ({
                downloaded: allowedCount,
                total: allowedCount + restrictedCount
            })),
            shareReplay()
        );

        this.hasImagesRestrictions = merge(
            this.imagesRestrictions.pipe(map(({ downloaded, total }) => total !== downloaded)),
            this.selectedSearchType.pipe(mapTo(false))
        ).pipe(shareReplay());

        this.missingProductDataInDownload = this.downloadInformation.pipe(
            filter(this.isBulkDownloadImagesInformation),
            map((downloadInformation) => (downloadInformation?.notFound > 0 ? downloadInformation : false)),
            shareReplay()
        );
    }

    public get userHasBasicPlan() {
        return this.userService.getPlan() === Plan.Basic;
    }

    public onClickDownload() {
        this.isErrorMessageVisible.next(false);

        if (this.hasMediaSelection && this.selectedCsvDownloadType) {
            return this.downloadImagesWithCsv();
        }
        if (this.hasMediaSelection) {
            return this.downloadImages();
        }
        if (this.selectedCsvDownloadType) {
            return this.downloadProductData();
        }
    }

    public onClickCancel() {
        this.contentService.toggleDownloadList();
    }

    public onDownloadTypeSelected(value: boolean, downloadType: DownloadEventType) {
        if (downloadType === DownloadEventType.DOWNLOAD_CSV) {
            this.selectedCsvDownloadType = value;
            this.updateCsvLanguageControlState();
        }
        if (downloadType === DownloadEventType.DOWNLOAD_IMAGES) {
            this.selectedImageDownloadType = value;
            this.updateResolutionControlState();
        }
        if (downloadType === DownloadEventType.DOWNLOAD_VIDEOS) {
            this.selectedVideoDownloadType = value;
        }
    }

    public get hasMediaSelection(): boolean {
        return this.selectedImageDownloadType || (this.selectedVideoDownloadType && this.videoDownloadEnabled());
    }

    public get isDownloadDisabled(): boolean {
        return !this.hasMediaSelection && !this.selectedCsvDownloadType;
    }

    public changeSearchType(searchType: SearchType) {
        this.selectedSearchType.next(searchType);
    }

    public onSearchTypeRadioChange(searchType: SearchType): void {
        this.multiDownloadForm?.get('selectedSearchType')?.setValue(searchType);
    }

    public onResolutionRadioChange(resolution: ImageResolution): void {
        this.multiDownloadForm?.get('selectedResolution')?.setValue(resolution);
    }

    public onCsvLanguageRadioChange(language: string): void {
        this.multiDownloadForm?.get('selectedCsvLanguage')?.setValue(language);
    }

    private downloadImages() {
        this.downloadTrigger.next({ type: DownloadEventType.DOWNLOAD_IMAGES });
    }

    private downloadImagesWithCsv() {
        this.downloadTrigger.next({ type: DownloadEventType.DOWNLOAD_IMAGES_WITH_CSV });
    }

    private downloadProductData() {
        this.downloadTrigger.next({ type: DownloadEventType.DOWNLOAD_CSV });
    }

    private getMaxAmountLimit(searchType: SearchType) {
        return searchType === SearchType.GTINS ? this.maxDownloadLimitGtin : this.maxDownloadLimitArticleNumber;
    }

    private isBulkDownloadImagesInformation(
        searchResult: BulkDownloadImagesInformation | boolean
    ): searchResult is BulkDownloadImagesInformation {
        return (searchResult as BulkDownloadImagesInformation).requested !== undefined;
    }

    private isBulkSearchResultWithInformation(
        searchResult: BulkDownloadStreamResult
    ): searchResult is BulkDownloadStreamResultWithInformation {
        return (searchResult as BulkSearchResultWithInformation).downloadInformation !== undefined;
    }

    private initForm() {
        this.multiDownloadForm = this.formBuilder.group({
            selectedResolution: [this.resolutionOptionsTranslated[0].value],
            selectedCsvLanguage: [this.csvLanguageOptionsTranslated[0].value],
            selectedSearchType: [this.selectedSearchType.value]
        });

        this.multiDownloadForm.get('selectedSearchType').valueChanges.subscribe((value) => {
            this.changeSearchType(value);
        });

        this.updateResolutionControlState();
        this.updateCsvLanguageControlState();
    }

    private updateResolutionControlState() {
        const control = this.multiDownloadForm.get('selectedResolution');
        if (this.selectedImageDownloadType) {
            control.enable({ emitEvent: false });
            return;
        }
        control.disable({ emitEvent: false });
    }

    private updateCsvLanguageControlState() {
        const control = this.multiDownloadForm.get('selectedCsvLanguage');
        if (this.selectedCsvDownloadType) {
            control.enable({ emitEvent: false });
            return;
        }
        control.disable({ emitEvent: false });
    }

    private addTracking(searchType: SearchType, items: string[]) {
        this.segmentService.track('Product data bulk search', {
            imageDownloadType: this.selectedImageDownloadType,
            videoDownloadType: this.selectedVideoDownloadType && this.videoDownloadEnabled(),
            csvDownloadType: this.multiDownloadForm.controls.selectedCsvLanguage.value,
            selectedImageQuality: this.multiDownloadForm.controls.selectedResolution.value,
            isMultiDownloadDrawer: this.isInMainContentDrawer,
            ...(searchType === SearchType.GTINS ? { requestedEans: items } : { requestedArticles: items }),
            ...(searchType === SearchType.GTINS ? { eanCount: _.size(items) } : { articlesCount: _.size(items) })
        });
    }

    private toBulkDownloadSearchBy(searchType: SearchType): BulkDownloadToastSearchBy {
        return searchType === SearchType.GTINS ? 'gtins' : 'articleNumbers';
    }

    private snapshotDownloadSelection(eventType: DownloadEventType): BulkDownloadToastSelection {
        const video = this.selectedVideoDownloadType && this.videoDownloadEnabled();

        switch (eventType) {
            case DownloadEventType.DOWNLOAD_CSV:
                return { images: false, video: false, csv: this.selectedCsvDownloadType };
            case DownloadEventType.DOWNLOAD_IMAGES:
                return { images: this.selectedImageDownloadType, video, csv: false };
            case DownloadEventType.DOWNLOAD_IMAGES_WITH_CSV:
                return { images: this.selectedImageDownloadType, video, csv: this.selectedCsvDownloadType };
            default:
                return { images: this.selectedImageDownloadType, video, csv: this.selectedCsvDownloadType };
        }
    }
}

enum DownloadEventType {
    DOWNLOAD_CSV = 'DOWNLOAD_CSV',
    DOWNLOAD_IMAGES = 'DOWNLOAD_IMAGES',
    DOWNLOAD_IMAGES_WITH_CSV = 'DOWNLOAD_IMAGES_WITH_CSV',
    DOWNLOAD_VIDEOS = 'DOWNLOAD_VIDEOS'
}

export enum SearchType {
    ARTICLE_NUMBERS = 'article_numbers',
    GTINS = 'gtins'
}

enum CsvLanguageOption {
    UserPreferredLanguage = 'preferredLanguage',
    All = 'all'
}
